import React, { useState, useEffect } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  CircularProgress,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogActions,
  Switch,
  FormControlLabel,
  Stack,
  FormGroup,
  Checkbox
} from '@mui/material';
import DialogContent from '@mui/material/DialogContent';
import {
  DataGrid,
  GridColDef,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridRowSelectionModel,
  useGridApiRef
} from '@mui/x-data-grid';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { useAppTheme } from '../hooks/useAppTheme';
import { usePersistedState } from '../hooks/usePersistedState';
import { effectiveSegment, isAutoSegment } from '../utils/sizeOrdersAs';

interface Instrument {
  id: number;
  symbol: string;
  name: string;
  exchange: string;
  instrumentType: string;
  lotSize: number;
  tickSize: number;
  leverage?: number;
  decimalPrecision?: number | null;
  strike?: number;
  expiry?: string;
  underlying?: string;
  currency: string;
  createdAt: string;
  updatedAt: string;
  // Editable fields
  indTracked?: boolean;
  indAutoTradeLong?: boolean;
  indAutoTradeShort?: boolean;
  indLiveFeed?: boolean;
  eqInvestmentAmount?: number;
  futInvestmentAmount?: number;
  optInvestmentAmountBuy?: number;
  optInvestmentAmountSell?: number;
  tradeSegment?: string;
  tradeMode?: string;
  currentPosition?: string;
  // Price level alerts
  nbuy?: number | null;
  nshort?: number | null;
  bbuy?: number | null;
  bshort?: number | null;
  // Watchlist flags
  wlLong?: boolean;
  wlShort?: boolean;
  // Legacy range-size (% of price) — kept on the API for old data; no UI.
  rangeSize?: number | null;
  // Favourite
  isFavourite?: boolean;
  // Live price
  currentPrice?: number | null;
}

interface ImportResponse {
  success: boolean;
  imported: number;
  processed: number;
  skipped: number;
  total: number;
  message: string;
}

const CustomToolbar = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
    </GridToolbarContainer>
  );
};

const InstrumentsPage: React.FC = () => {
  const { C } = useAppTheme();
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingZerodha, setImportingZerodha] = useState(false);
  const [zerodhaDialogOpen, setZerodhaDialogOpen] = useState(false);
  const [zerodhaImportExchange, setZerodhaImportExchange] = useState('ALL');
  const [zerodhaImportType, setZerodhaImportType] = useState('ALL');
  // Filters — persisted so they survive navigating away and back (issue #2).
  const [searchTerm, setSearchTerm] = usePersistedState('instruments.searchTerm', '');
  const [exchangeFilter, setExchangeFilter] = usePersistedState('instruments.exchangeFilter', 'all');
  const [typeFilter, setTypeFilter] = usePersistedState('instruments.typeFilter', 'all');
  const [filterTracked, setFilterTracked] = usePersistedState('instruments.filterTracked', false);
  const [filterLiveFeed, setFilterLiveFeed] = usePersistedState('instruments.filterLiveFeed', false);
  const [filterAutoLong, setFilterAutoLong] = usePersistedState('instruments.filterAutoLong', false);
  const [filterAutoShort, setFilterAutoShort] = usePersistedState('instruments.filterAutoShort', false);
  const [filterFavourites, setFilterFavourites] = usePersistedState('instruments.filterFavourites', false);
  // Readymade-filter extras: untracked-only, and NSE F&O-underlying stocks
  // (NSE EQ whose symbol is an NFO underlying name).
  const [filterUntracked, setFilterUntracked] = usePersistedState('instruments.filterUntracked', false);
  const [filterFnoUnderlying, setFilterFnoUnderlying] = usePersistedState('instruments.filterFnoUnderlying', false);
  const [xlsxExporting, setXlsxExporting] = useState(false);
  const [xlsxRestoring, setXlsxRestoring] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const apiRef = useGridApiRef();
  const [expiryFilter, setExpiryFilter] = useState('');
  const [strikeMinFilter, setStrikeMinFilter] = useState('');
  const [strikeMaxFilter, setStrikeMaxFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingInstrument, setEditingInstrument] = useState<Instrument | null>(null);
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // "Increase Investment by N%" — grows the per-symbol investment amounts for a chosen
  // set of types across the selected instruments (used as the portfolio scales up).
  const [scaleInvestOpen, setScaleInvestOpen] = useState(false);
  const [scaleInvestPercent, setScaleInvestPercent] = useState('10');
  const [scaleInvestFields, setScaleInvestFields] = useState({
    eqInvestmentAmount: true,
    futInvestmentAmount: true,
    optInvestmentAmountBuy: true,
    optInvestmentAmountSell: true,
  });
  const [bulkEditValues, setBulkEditValues] = useState({
    eqInvestmentAmount: '',
    futInvestmentAmount: '',
    optInvestmentAmountBuy: '',
    optInvestmentAmountSell: '',
    leverage: '',
    decimalPrecision: '',
    indTracked: '',
    indLiveFeed: '',
    indAutoTradeLong: '',
    indAutoTradeShort: '',
    tradeSegment: '',
    tradeMode: '',
    wlLong: '',
    wlShort: '',
    isFavourite: ''
  });
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'info' });

  const fetchInstruments = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: page + 1,
        pageSize
      };

      if (searchTerm) params.search = searchTerm;
      if (exchangeFilter !== 'all') params.exchange = exchangeFilter;
      if (typeFilter !== 'all') params.type = typeFilter;
      if (filterTracked) params.tracked = 'true';
      else if (filterUntracked) params.tracked = 'false';
      if (filterFnoUnderlying) params.fnoUnderlying = 'true';
      if (filterLiveFeed) params.liveFeed = 'true';
      if (filterAutoLong) params.autoLong = 'true';
      if (filterAutoShort) params.autoShort = 'true';
      if (filterFavourites) params.favourites = 'true';
      if (expiryFilter) params.expiry = expiryFilter;
      if (strikeMinFilter) params.strikeMin = strikeMinFilter;
      if (strikeMaxFilter) params.strikeMax = strikeMaxFilter;

      const response = await api.get<{
        instruments: Instrument[];
        total: number;
      }>('/instruments', { params });

      // Debug: Log first instrument to check data types
      if (response.data.instruments && response.data.instruments.length > 0) {
        const first = response.data.instruments[0];
        console.log('DEBUG - First instrument:', {
          symbol: first.symbol,
          lotSize: first.lotSize,
          lotSizeType: typeof first.lotSize,
          tickSize: first.tickSize,
          tickSizeType: typeof first.tickSize,
          leverage: first.leverage,
          leverageType: typeof first.leverage
        });
      }

      setInstruments(response.data.instruments || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching instruments:', error);
      showSnackbar('Failed to fetch instruments', 'error');
      setInstruments([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleImportFromDelta = async () => {
    const confirmed = window.confirm(
      '🔄 LOAD DELTA EXCHANGE SYMBOLS?\n\n' +
      'This will:\n' +
      '  • Connect to Delta Exchange India API\n' +
      '  • Download current cryptocurrency instrument list\n' +
      '  • Update local database with Delta symbols\n' +
      '  • Refresh the symbols display\n\n' +
      '⏱️ This may take several minutes and requires API connectivity.\n\n' +
      'Continue with Delta Exchange symbol import?'
    );

    if (!confirmed) return;

    try {
      setImporting(true);
      showSnackbar('Connecting to Delta Exchange API...', 'info');

      const response = await api.post<ImportResponse>('/instruments/import/delta');

      if (response.data.success) {
        showSnackbar(
          `✅ Successfully imported ${response.data.imported} instruments (${response.data.processed} processed, ${response.data.skipped} skipped)`,
          'success'
        );
        // Refresh the list
        await fetchInstruments();
      } else {
        showSnackbar('Import failed: ' + (response.data.message || 'Unknown error'), 'error');
      }
    } catch (error: any) {
      console.error('Error importing instruments:', error);
      const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to import instruments';
      showSnackbar(errorMessage, 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleImportFromZerodha = async () => {
    setZerodhaDialogOpen(false);
    try {
      setImportingZerodha(true);
      showSnackbar(`Fetching instruments from Zerodha (Exchange: ${zerodhaImportExchange}, Type: ${zerodhaImportType})...`, 'info');
      const params: any = {
        exchangeFilter: zerodhaImportExchange,
        typeFilter: zerodhaImportType
      };
      const response = await api.post<ImportResponse>('/instruments/import/zerodha', {}, { params, timeout: 180000 });
      if (response.data.success) {
        showSnackbar(
          `Successfully imported ${(response.data as any).data?.processed || response.data.imported} instruments from Zerodha (${(response.data as any).data?.skipped || response.data.skipped} skipped)`,
          'success'
        );
        await fetchInstruments();
      } else {
        showSnackbar('Zerodha import failed: ' + (response.data.message || 'Unknown error'), 'error');
      }
    } catch (error: any) {
      console.error('Error importing from Zerodha:', error);
      const errorMessage = error.response?.data?.error?.message || error.message || 'Failed to import Zerodha instruments';
      showSnackbar(errorMessage, 'error');
    } finally {
      setImportingZerodha(false);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      showSnackbar('Please select instruments to delete', 'info');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedRows.length} instrument(s)?\n\nThis action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      setLoading(true);
      await api.post('/instruments/bulk-delete', { ids: selectedRows });

      showSnackbar(`Successfully deleted ${selectedRows.length} instrument(s)`, 'success');
      setSelectedRows([]);
      await fetchInstruments();
    } catch (error: any) {
      console.error('Error deleting instruments:', error);
      const errorMessage = error.response?.data?.error?.message || 'Failed to delete instruments';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (instrument: Instrument) => {
    setEditingInstrument(instrument);
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingInstrument) return;

    try {
      setLoading(true);
      await api.put(`/instruments/${editingInstrument.id}`, editingInstrument);

      showSnackbar('Instrument updated successfully', 'success');
      setEditDialogOpen(false);
      setEditingInstrument(null);
      await fetchInstruments();
    } catch (error: any) {
      console.error('Error updating instrument:', error);
      const errorMessage = error.response?.data?.error?.message || 'Failed to update instrument';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditingInstrument(null);
  };

  const handleSelectAllFiltered = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      if (exchangeFilter !== 'all') params.exchange = exchangeFilter;
      if (typeFilter !== 'all') params.type = typeFilter;

      const response = await api.post<{ success: boolean; ids: number[]; total: number }>(
        '/instruments/filtered-ids',
        params
      );

      if (response.data.success && response.data.ids.length > 0) {
        setSelectedRows(response.data.ids);
        showSnackbar(`Selected all ${response.data.total} filtered instruments`, 'success');
      } else {
        showSnackbar('No instruments match the current filters', 'info');
      }
    } catch (error: any) {
      console.error('Error selecting all filtered instruments:', error);
      showSnackbar('Failed to select all filtered instruments', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEditOpen = () => {
    setBulkEditValues({
      eqInvestmentAmount: '',
      futInvestmentAmount: '',
      optInvestmentAmountBuy: '',
      optInvestmentAmountSell: '',
      leverage: '',
      decimalPrecision: '',
      indTracked: '',
      indLiveFeed: '',
      indAutoTradeLong: '',
      indAutoTradeShort: '',
      tradeSegment: '',
      tradeMode: '',
      wlLong: '',
      wlShort: '',
      isFavourite: ''
    });
    setBulkEditOpen(true);
  };

  const handleBulkEditSave = async () => {
    try {
      setLoading(true);
      // Build updates object, only including non-empty values
      const updates: any = {};
      if (bulkEditValues.eqInvestmentAmount !== '') updates.eqInvestmentAmount = parseFloat(bulkEditValues.eqInvestmentAmount);
      if (bulkEditValues.futInvestmentAmount !== '') updates.futInvestmentAmount = parseFloat(bulkEditValues.futInvestmentAmount);
      if (bulkEditValues.optInvestmentAmountBuy !== '') updates.optInvestmentAmountBuy = parseFloat(bulkEditValues.optInvestmentAmountBuy);
      if (bulkEditValues.optInvestmentAmountSell !== '') updates.optInvestmentAmountSell = parseFloat(bulkEditValues.optInvestmentAmountSell);
      if (bulkEditValues.leverage !== '') updates.leverage = parseFloat(bulkEditValues.leverage);
      if (bulkEditValues.decimalPrecision !== '') {
        const dpN = parseInt(bulkEditValues.decimalPrecision, 10);
        if (Number.isFinite(dpN)) updates.decimalPrecision = Math.max(0, Math.min(8, dpN));
      }
      if (bulkEditValues.indTracked !== '') updates.indTracked = bulkEditValues.indTracked === 'true';
      if (bulkEditValues.indLiveFeed !== '') updates.indLiveFeed = bulkEditValues.indLiveFeed === 'true';
      if (bulkEditValues.indAutoTradeLong !== '') updates.indAutoTradeLong = bulkEditValues.indAutoTradeLong === 'true';
      if (bulkEditValues.indAutoTradeShort !== '') updates.indAutoTradeShort = bulkEditValues.indAutoTradeShort === 'true';
      if (bulkEditValues.tradeSegment !== '') updates.tradeSegment = bulkEditValues.tradeSegment === 'AUTO' ? null : bulkEditValues.tradeSegment;
      if (bulkEditValues.tradeMode !== '') updates.tradeMode = bulkEditValues.tradeMode;
      if (bulkEditValues.wlLong !== '') updates.wlLong = bulkEditValues.wlLong === 'true';
      if (bulkEditValues.wlShort !== '') updates.wlShort = bulkEditValues.wlShort === 'true';
      if (bulkEditValues.isFavourite !== '') updates.isFavourite = bulkEditValues.isFavourite === 'true';

      if (Object.keys(updates).length === 0) {
        showSnackbar('No fields to update. Please fill in at least one field.', 'info');
        setLoading(false);
        return;
      }

      await api.post('/instruments/bulk-update', { ids: selectedRows, updates });
      showSnackbar(`Successfully updated ${selectedRows.length} instrument(s)`, 'success');
      setBulkEditOpen(false);
      setSelectedRows([]);
      await fetchInstruments();
    } catch (error: any) {
      console.error('Error bulk updating instruments:', error);
      const errorMessage = error.response?.data?.error?.message || 'Failed to bulk update instruments';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleScaleInvestSave = async () => {
    const pct = parseFloat(scaleInvestPercent);
    if (!Number.isFinite(pct) || pct <= -100) {
      showSnackbar('Enter a valid percentage greater than -100.', 'error');
      return;
    }
    const fields = (Object.keys(scaleInvestFields) as (keyof typeof scaleInvestFields)[]).filter(k => scaleInvestFields[k]);
    if (fields.length === 0) {
      showSnackbar('Select at least one investment type to increase.', 'info');
      return;
    }
    try {
      setLoading(true);
      const res = await api.post('/instruments/bulk-scale-investment', { ids: selectedRows, percent: pct, fields });
      const updated = res.data?.data?.updated ?? 0;
      showSnackbar(
        `${pct >= 0 ? 'Increased' : 'Reduced'} investment by ${Math.abs(pct)}% on ${updated} instrument(s)` +
        (updated < selectedRows.length ? ` (${selectedRows.length - updated} had no matching amount set)` : ''),
        'success'
      );
      setScaleInvestOpen(false);
      setSelectedRows([]);
      await fetchInstruments();
    } catch (error: any) {
      const errorMessage = error.response?.data?.error?.message || 'Failed to scale investment amounts';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleToggleFavourite = async (instrument: Instrument) => {
    try {
      await api.put(`/instruments/${instrument.id}`, { isFavourite: !instrument.isFavourite });
      setInstruments(prev => prev.map(i => i.id === instrument.id ? { ...i, isFavourite: !i.isFavourite } : i));
    } catch (e) {
      showSnackbar('Failed to update favourite', 'error');
    }
  };

  const handleExcelExport = async () => {
    try {
      setXlsxExporting(true);
      showSnackbar('Preparing Excel export...', 'info');
      // Fetch ALL instruments (no pagination limit) — extended timeout for large datasets
      const resp = await api.get<{ instruments: Instrument[]; total: number }>('/instruments', {
        params: { page: 1, pageSize: 99999 },
        timeout: 120000
      });
      const rows = resp.data.instruments || [];

      const exportCols = [
        'id','symbol','exchange','name','instrumentType','segment',
        'isFavourite','nbuy','nshort','bbuy','bshort',
        'wlLong','wlShort','tradeSegment','tradeMode',
        'indAutoTradeLong','indAutoTradeShort','indTracked','indLiveFeed',
        'eqInvestmentAmount','futInvestmentAmount','optInvestmentAmountBuy','optInvestmentAmountSell',
        'currentPosition','lotSize','tickSize','leverage','decimalPrecision','rangeSize','strike','expiry',
        'underlying','currency','instrumentToken','exchangeToken','currentPrice'
      ];

      const wsData = [
        exportCols, // header row
        ...rows.map(r => exportCols.map(col => (r as any)[col] ?? ''))
      ];

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      // Set column widths
      ws['!cols'] = exportCols.map(c => ({ wch: ['nbuy','nshort','bbuy','bshort','isFavourite'].includes(c) ? 12 : ['symbol','name'].includes(c) ? 20 : 15 }));
      XLSX.utils.book_append_sheet(wb, ws, 'Instruments');
      XLSX.writeFile(wb, `instruments_backup_${new Date().toISOString().slice(0,10)}.xlsx`);
      showSnackbar(`Exported ${rows.length} instruments to Excel`, 'success');
    } catch (e: any) {
      showSnackbar('Export failed: ' + (e.message || 'Unknown error'), 'error');
    } finally {
      setXlsxExporting(false);
    }
  };

  const handleExcelRestore = async (file: File) => {
    try {
      setXlsxRestoring(true);
      showSnackbar('Parsing Excel file...', 'info');
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(ws, { defval: '' });

      if (!rows.length) { showSnackbar('No data found in Excel file', 'error'); return; }

      // Parse booleans
      const boolFields = ['isFavourite','wlLong','wlShort','indAutoTradeLong','indAutoTradeShort','indTracked','indLiveFeed'];
      const numFields = ['nbuy','nshort','bbuy','bshort','eqInvestmentAmount','futInvestmentAmount','optInvestmentAmountBuy','optInvestmentAmountSell','lotSize','tickSize','leverage','decimalPrecision','rangeSize','strike','currentPrice'];
      const parsed = rows.map(r => {
        const obj: any = { symbol: r.symbol, exchange: r.exchange };
        boolFields.forEach(f => { if (r[f] !== '') obj[f] = r[f] === true || r[f] === 'true' || r[f] === 1 || r[f] === '1'; });
        numFields.forEach(f => { const v = parseFloat(r[f]); if (!isNaN(v)) obj[f] = v; });
        ['tradeSegment','tradeMode','currentPosition','name'].forEach(f => { if (r[f] !== undefined && r[f] !== '') obj[f] = r[f]; });
        return obj;
      });

      showSnackbar(`Restoring ${parsed.length} instruments...`, 'info');
      const resp = await api.post<any>('/instruments/restore-backup', { instruments: parsed });
      const { updated, skipped } = resp.data.data || resp.data;
      showSnackbar(`Restore complete: ${updated} updated, ${skipped} skipped (not found in DB)`, 'success');
      await fetchInstruments();
    } catch (e: any) {
      showSnackbar('Restore failed: ' + (e.response?.data?.error?.message || e.message || 'Unknown error'), 'error');
    } finally {
      setXlsxRestoring(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const processRowUpdate = async (newRow: Instrument, oldRow: Instrument) => {
    try {
      // Clamp decimalPrecision to a safe integer range (0..8) before persisting.
      // The grid edits give a free-form number; backend stores the value as-is and
      // the UI uses it everywhere, so an out-of-range value would render badly.
      const cleaned: any = { ...newRow };
      if (cleaned.decimalPrecision != null && cleaned.decimalPrecision !== '') {
        const n = parseInt(String(cleaned.decimalPrecision), 10);
        cleaned.decimalPrecision = Number.isFinite(n) ? Math.max(0, Math.min(8, n)) : oldRow.decimalPrecision;
      }
      if (cleaned.rangeSize !== undefined && cleaned.rangeSize !== null && cleaned.rangeSize !== '') {
        const n = parseFloat(String(cleaned.rangeSize));
        cleaned.rangeSize = Number.isFinite(n) && n > 0 ? n : oldRow.rangeSize;
      }
      await api.put(`/instruments/${cleaned.id}`, cleaned);
      showSnackbar('Instrument updated successfully', 'success');
      return cleaned as Instrument;
    } catch (error: any) {
      console.error('Error updating instrument:', error);
      const errorMessage = error.response?.data?.error?.message || 'Failed to update instrument';
      showSnackbar(errorMessage, 'error');
      return oldRow; // Return old row to revert changes
    }
  };

  const handleProcessRowUpdateError = (error: Error) => {
    showSnackbar(error.message || 'Error updating row', 'error');
  };

  useEffect(() => {
    fetchInstruments();
  }, [page, pageSize, exchangeFilter, typeFilter, filterTracked, filterUntracked, filterFnoUnderlying, filterLiveFeed, filterAutoLong, filterAutoShort, filterFavourites, expiryFilter, strikeMinFilter, strikeMaxFilter]);

  useEffect(() => {
    // Debounce search
    const timer = setTimeout(() => {
      if (page === 0) {
        fetchInstruments();
      } else {
        setPage(0); // Reset to first page on search
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const numFmt = (v: any) => { if (v == null) return '-'; const n = typeof v === 'string' ? parseFloat(v) : v; return isNaN(n) ? '-' : n >= 1 ? n.toFixed(2) : n >= 0.01 ? n.toFixed(4) : n.toFixed(6); };
  const rupFmt = (v: any) => { if (v == null) return '-'; const n = typeof v === 'string' ? parseFloat(v) : v; return !isNaN(n) ? `₹${n.toFixed(0)}` : '-'; };
  const plFmt = (v: any) => v ? Number(v).toFixed(2) : '-';

  const columns: GridColDef[] = [
    // Actions (edit dialog)
    {
      field: 'actions', headerName: '', width: 50, sortable: false, filterable: false,
      renderCell: (params) => (
        <Tooltip title="Edit">
          <IconButton size="small" color="primary" onClick={() => handleEditClick(params.row)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )
    },
    // 1. Symbol (pinned)
    {
      field: 'symbol', headerName: 'Symbol', width: 130,
      renderCell: (params) => <Typography variant="body2" fontWeight={700} sx={{ color: C.neutral }}>{params.value}</Typography>
    },
    // 2. Favourite (star toggle)
    {
      field: 'isFavourite', headerName: '⭐', width: 55, sortable: true, filterable: false,
      renderCell: (params: any) => (
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleFavourite(params.row); }}
          sx={{ color: params.value ? C.warn : C.dimMuted }}>
          {params.value ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
        </IconButton>
      )
    },
    // 3. NBUY
    {
      field: 'nbuy', headerName: 'NBUY (≤)', width: 100, type: 'number', editable: true, align: 'right', headerAlign: 'right',
      renderHeader: () => <Tooltip title="Normal Buy: alert when price ≤ this level"><Box sx={{ display:'flex',alignItems:'center',gap:0.5,cursor:'help' }}><span style={{color:C.bull,fontWeight:700}}>NBUY</span><InfoOutlined sx={{fontSize:'0.7rem',color:C.bull}}/></Box></Tooltip>,
      valueFormatter: plFmt, renderCell: (p:any) => p.value ? <Typography variant="caption" sx={{color:C.bull,fontWeight:600}}>{Number(p.value).toFixed(2)}</Typography> : null
    },
    // 4. NSHORT
    {
      field: 'nshort', headerName: 'NSHORT (≥)', width: 100, type: 'number', editable: true, align: 'right', headerAlign: 'right',
      renderHeader: () => <Tooltip title="Normal Short: alert when price ≥ this level"><Box sx={{ display:'flex',alignItems:'center',gap:0.5,cursor:'help' }}><span style={{color:C.bear,fontWeight:700}}>NSHORT</span><InfoOutlined sx={{fontSize:'0.7rem',color:C.bear}}/></Box></Tooltip>,
      valueFormatter: plFmt, renderCell: (p:any) => p.value ? <Typography variant="caption" sx={{color:C.bear,fontWeight:600}}>{Number(p.value).toFixed(2)}</Typography> : null
    },
    // 5. BBUY
    {
      field: 'bbuy', headerName: 'BBUY (≥)', width: 100, type: 'number', editable: true, align: 'right', headerAlign: 'right',
      renderHeader: () => <Tooltip title="Breakout Buy: alert when price ≥ this level"><Box sx={{ display:'flex',alignItems:'center',gap:0.5,cursor:'help' }}><span style={{color:C.neutral,fontWeight:700}}>BBUY</span><InfoOutlined sx={{fontSize:'0.7rem',color:C.neutral}}/></Box></Tooltip>,
      valueFormatter: plFmt, renderCell: (p:any) => p.value ? <Typography variant="caption" sx={{color:C.neutral,fontWeight:600}}>{Number(p.value).toFixed(2)}</Typography> : null
    },
    // 6. BSHORT
    {
      field: 'bshort', headerName: 'BSHORT (≤)', width: 100, type: 'number', editable: true, align: 'right', headerAlign: 'right',
      renderHeader: () => <Tooltip title="Breakout Short: alert when price ≤ this level"><Box sx={{ display:'flex',alignItems:'center',gap:0.5,cursor:'help' }}><span style={{color:C.accent,fontWeight:700}}>BSHORT</span><InfoOutlined sx={{fontSize:'0.7rem',color:C.accent}}/></Box></Tooltip>,
      valueFormatter: plFmt, renderCell: (p:any) => p.value ? <Typography variant="caption" sx={{color:C.accent,fontWeight:600}}>{Number(p.value).toFixed(2)}</Typography> : null
    },
    // 7. WL Long
    {
      field: 'wlLong', headerName: 'WL Long', width: 85, type: 'boolean', editable: true,
      renderCell: (p:any) => p.value ? <Chip label="L" size="small" sx={{bgcolor:C.bullMuted,color:C.bull,fontWeight:700,fontSize:'0.65rem',height:18}}/> : null
    },
    // 8. WL Short
    {
      field: 'wlShort', headerName: 'WL Short', width: 85, type: 'boolean', editable: true,
      renderCell: (p:any) => p.value ? <Chip label="S" size="small" sx={{bgcolor:C.bearMuted,color:C.bear,fontWeight:700,fontSize:'0.65rem',height:18}}/> : null
    },
    // 9. Size orders as (was "Trade Segment"). Empty = Auto: Futures on a derivatives
    //    venue (Delta, NFO, BFO, MCX, CDS), Equity otherwise — the server's own rule.
    //    Options are no longer a segment: the order dialog offers its Strategy tab on any
    //    symbol with listed options, and a price level can open an option strategy (⚡).
    { field: 'tradeSegment', headerName: 'Size as', width: 110, editable: true, type: 'singleSelect',
      valueOptions: [{ value: '', label: 'Auto' }, { value: 'EQUITY', label: 'Equity' }, { value: 'FUTURES', label: 'Futures' }],
      renderHeader: () => <Tooltip title="Which budget sizes an order in this symbol itself: Equity (EQ investment) or Futures (FUT investment). Auto = Futures on Delta / NFO / BFO / MCX / CDS, Equity otherwise. Option trades are not chosen here — use the Strategy tab in the order dialog or the ⚡ on a price level."><Box sx={{ display:'flex',alignItems:'center',gap:0.5,cursor:'help' }}><span>Size as</span><InfoOutlined sx={{fontSize:'0.7rem'}}/></Box></Tooltip>,
      renderCell: (p: any) => { const v = String(p.value || '').toUpperCase(); return v === 'EQUITY' ? 'Equity' : v === 'FUTURES' ? 'Futures' : <span style={{ opacity: 0.6 }}>Auto · {effectiveSegment(p.row?.exchange, p.row?.instrumentType, '') === 'FUTURES' ? 'Futures' : 'Equity'}</span>; } },
    // 10. Trade Mode — only Naked (an outright order) is sized by the server.
    { field: 'tradeMode', headerName: 'Mode', width: 90, editable: true, type: 'singleSelect', valueOptions: [{ value: '', label: 'Naked' }, { value: 'Naked', label: 'Naked' }],
      renderCell: () => 'Naked' },
    // 11. Auto Long
    { field: 'indAutoTradeLong', headerName: 'AT Long', width: 80, type: 'boolean', editable: true,
      renderHeader: () => <Tooltip title="Auto-trade Long — when ON, RockBand BUY signals for this symbol auto-execute (Auto Trade + Order Placement must also be ON). OFF → BUY alerts are skipped with an AUTO SKIPPED · AL OFF chip; you can still Execute them manually."><Box sx={{ display:'flex',alignItems:'center',gap:0.5,cursor:'help' }}><span style={{color:C.bull,fontWeight:700}}>AT Long</span><InfoOutlined sx={{fontSize:'0.7rem',color:C.bull}}/></Box></Tooltip> },
    // 12. Auto Short
    { field: 'indAutoTradeShort', headerName: 'AT Short', width: 80, type: 'boolean', editable: true,
      renderHeader: () => <Tooltip title="Auto-trade Short — when ON, RockBand SHORT signals for this symbol auto-execute (Auto Trade + Order Placement must also be ON). OFF → SHORT alerts are skipped with an AUTO SKIPPED · AS OFF chip; you can still Execute them manually."><Box sx={{ display:'flex',alignItems:'center',gap:0.5,cursor:'help' }}><span style={{color:C.bear,fontWeight:700}}>AT Short</span><InfoOutlined sx={{fontSize:'0.7rem',color:C.bear}}/></Box></Tooltip> },
    // 13-16. Investment amounts
    { field: 'eqInvestmentAmount', headerName: 'EQ Inv', width: 95, type: 'number', editable: true, align: 'right', headerAlign: 'right', valueFormatter: rupFmt },
    { field: 'futInvestmentAmount', headerName: 'FUT Inv', width: 95, type: 'number', editable: true, align: 'right', headerAlign: 'right', valueFormatter: rupFmt },
    { field: 'optInvestmentAmountBuy', headerName: 'OPT Buy', width: 95, type: 'number', editable: true, align: 'right', headerAlign: 'right', valueFormatter: rupFmt },
    { field: 'optInvestmentAmountSell', headerName: 'OPT Sell', width: 95, type: 'number', editable: true, align: 'right', headerAlign: 'right', valueFormatter: rupFmt },
    // Remaining columns
    { field: 'exchange', headerName: 'Exchange', width: 100, renderCell: (p:any) => <Chip label={p.value} size="small" color="primary" variant="outlined"/> },
    { field: 'instrumentType', headerName: 'Type', width: 110, renderCell: (p:any) => <Chip label={p.value} size="small" variant="outlined"/> },
    { field: 'name', headerName: 'Name', width: 180, editable: true },
    { field: 'currentPosition', headerName: 'Position', width: 100, editable: true, type: 'singleSelect', valueOptions: ['','LONG','SHORT','FLAT'] },
    { field: 'indTracked', headerName: 'Tracked', width: 85, type: 'boolean', editable: true },
    { field: 'indLiveFeed', headerName: 'Live Feed', width: 85, type: 'boolean', editable: true },
    { field: 'currentPrice', headerName: 'Price', width: 95, align: 'right', headerAlign: 'right', valueFormatter: (v:any) => v ? `₹${Number(v).toFixed(2)}` : '-' },
    { field: 'lotSize', headerName: 'Lot Size', width: 95, align: 'right', headerAlign: 'right', valueFormatter: numFmt },
    { field: 'tickSize', headerName: 'Tick Size', width: 95, align: 'right', headerAlign: 'right', valueFormatter: numFmt },
    { field: 'leverage', headerName: 'Leverage', width: 85, align: 'right', headerAlign: 'right', valueFormatter: (v:any) => { if (v==null) return '-'; const n=typeof v==='string'?parseFloat(v):v; return !isNaN(n)?`${n}x`:'-'; } },
    {
      field: 'decimalPrecision',
      headerName: 'Decimals',
      width: 90,
      type: 'number',
      editable: true,
      align: 'right',
      headerAlign: 'right',
      // Double-click cell to edit; value is integer 0..8. Clamp on commit.
      valueFormatter: (v:any) => {
        if (v == null || v === '') return '-';
        const n = typeof v === 'string' ? parseInt(v, 10) : v;
        return Number.isFinite(n) ? String(Math.max(0, Math.min(8, n))) : '-';
      },
    },
    { field: 'strike', headerName: 'Strike', width: 90, align: 'right', headerAlign: 'right', valueFormatter: (v:any) => { if (v==null) return '-'; const n=typeof v==='string'?parseFloat(v):v; return !isNaN(n)?n.toFixed(2):'-'; } },
    { field: 'expiry', headerName: 'Expiry', width: 110, valueFormatter: (v:any) => v ? new Date(v).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'numeric'}) : '-' },
    { field: 'underlying', headerName: 'Underlying', width: 110 },
    { field: 'currency', headerName: 'Ccy', width: 70 },
    { field: 'instrumentToken', headerName: 'Token', width: 90 },
    { field: 'updatedAt', headerName: 'Updated', width: 140, valueFormatter: (v:any) => v ? new Date(v).toLocaleDateString('en-IN',{day:'2-digit',month:'short',year:'2-digit'}) : '-' }
  ];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="h4" fontWeight="bold">
            Instruments
          </Typography>
          <Tooltip title="How does this page work?">
            <IconButton size="small" onClick={() => setHelpOpen(true)} sx={{ ml: 0.5 }}>
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<SelectAllIcon />}
            onClick={handleSelectAllFiltered}
            disabled={loading || total === 0}
          >
            Select All Filtered ({total})
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<EditIcon />}
            onClick={handleBulkEditOpen}
            disabled={loading || selectedRows.length === 0}
          >
            Bulk Edit ({selectedRows.length})
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<TrendingUpIcon />}
            onClick={() => setScaleInvestOpen(true)}
            disabled={loading || selectedRows.length === 0}
          >
            Increase Investment ({selectedRows.length})
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleDeleteSelected}
            disabled={loading || selectedRows.length === 0}
          >
            Delete Selected ({selectedRows.length})
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={xlsxExporting ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
            onClick={handleExcelExport}
            disabled={xlsxExporting}
          >
            {xlsxExporting ? 'Exporting...' : 'Export Excel'}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={xlsxRestoring ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={xlsxRestoring}
          >
            {xlsxRestoring ? 'Restoring...' : 'Restore Excel'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: 'none' }}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleExcelRestore(f); }}
          />
          <Button
            variant="contained"
            color="secondary"
            startIcon={importing ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
            onClick={handleImportFromDelta}
            disabled={importing}
          >
            {importing ? 'Importing...' : 'Import from Delta Exchange'}
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={importingZerodha ? <CircularProgress size={20} color="inherit" /> : <CloudDownloadIcon />}
            onClick={() => setZerodhaDialogOpen(true)}
            disabled={importingZerodha}
          >
            {importingZerodha ? 'Importing...' : 'Import Zerodha (NSE)'}
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchInstruments} disabled={loading} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Box>
            <TextField
              size="small"
              placeholder="Search symbol..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value.toUpperCase())}
              sx={{ minWidth: 200 }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />
              }}
            />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
              Examples: <b>NIFTY</b> · <b>BTC</b> · <b>ETH</b> · <b>NIFTY25</b> (expiry year) · <b>BTCUSDT</b> (exact) · <b>NIFTY%</b> (prefix)
            </Typography>
          </Box>
          {/* Readymade quick filters */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', width: '100%' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mr: 0.5 }}>Quick:</Typography>
            <Button size="small" variant="outlined" sx={{ fontSize: '0.7rem', py: 0.2, textTransform: 'none' }}
              onClick={() => { setExchangeFilter('DELTA'); setTypeFilter('PERPETUAL'); setFilterFnoUnderlying(false); setFilterTracked(false); setFilterUntracked(true); setPage(0); }}>
              Delta Perpetual · Not Tracked
            </Button>
            <Button size="small" variant="outlined" sx={{ fontSize: '0.7rem', py: 0.2, textTransform: 'none' }}
              onClick={() => { setExchangeFilter('NSE'); setTypeFilter('EQ'); setFilterFnoUnderlying(true); setFilterTracked(true); setFilterUntracked(false); setPage(0); }}>
              NSE F&O · Tracked
            </Button>
            <Button size="small" variant="outlined" sx={{ fontSize: '0.7rem', py: 0.2, textTransform: 'none' }}
              onClick={() => { setExchangeFilter('NSE'); setTypeFilter('EQ'); setFilterFnoUnderlying(true); setFilterTracked(false); setFilterUntracked(true); setPage(0); }}>
              NSE F&O · Not Tracked
            </Button>
            <Button size="small" sx={{ fontSize: '0.7rem', py: 0.2, textTransform: 'none', color: 'text.secondary' }}
              onClick={() => { setExchangeFilter('all'); setTypeFilter('all'); setFilterFnoUnderlying(false); setFilterTracked(false); setFilterUntracked(false); setSearchTerm(''); setPage(0); }}>
              Clear
            </Button>
          </Box>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Exchange</InputLabel>
            <Select
              value={exchangeFilter}
              label="Exchange"
              onChange={(e) => { setExchangeFilter(e.target.value); setFilterFnoUnderlying(false); }}
            >
              <MenuItem value="all">All Exchanges</MenuItem>
              <MenuItem value="DELTA">Delta Exchange</MenuItem>
              <MenuItem value="NSE">NSE</MenuItem>
              <MenuItem value="BSE">BSE</MenuItem>
              <MenuItem value="NFO">NFO (F&O)</MenuItem>
              <MenuItem value="BFO">BFO (BSE F&O)</MenuItem>
              <MenuItem value="CDS">CDS (Currency)</MenuItem>
              <MenuItem value="MCX">MCX (Commodity)</MenuItem>
              <MenuItem value="BCD">BCD</MenuItem>
              <MenuItem value="GLOBAL">GLOBAL</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={typeFilter}
              label="Type"
              onChange={(e) => setTypeFilter(e.target.value)}
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="EQ">EQ (Equity)</MenuItem>
              <MenuItem value="FUT">FUT (Futures)</MenuItem>
              <MenuItem value="CE">CE (Call Option)</MenuItem>
              <MenuItem value="PE">PE (Put Option)</MenuItem>
              <MenuItem value="PERPETUAL">Perpetual</MenuItem>
              <MenuItem value="SPOT">Spot</MenuItem>
            </Select>
          </FormControl>
          <FormGroup row sx={{ gap: 0.5 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filterTracked}
                  onChange={(e) => { setFilterTracked(e.target.checked); if (e.target.checked) setFilterUntracked(false); setPage(0); }}
                />
              }
              label={<Typography variant="caption">Tracked</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filterUntracked}
                  onChange={(e) => { setFilterUntracked(e.target.checked); if (e.target.checked) setFilterTracked(false); setPage(0); }}
                />
              }
              label={<Typography variant="caption">Not Tracked</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filterLiveFeed}
                  onChange={(e) => { setFilterLiveFeed(e.target.checked); setPage(0); }}
                />
              }
              label={<Typography variant="caption">Live Feed</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filterAutoLong}
                  onChange={(e) => { setFilterAutoLong(e.target.checked); setPage(0); }}
                />
              }
              label={<Typography variant="caption">Auto Long</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filterAutoShort}
                  onChange={(e) => { setFilterAutoShort(e.target.checked); setPage(0); }}
                />
              }
              label={<Typography variant="caption">Auto Short</Typography>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={filterFavourites}
                  onChange={(e) => { setFilterFavourites(e.target.checked); setPage(0); }}
                  sx={{ color: C.warn, '&.Mui-checked': { color: C.warn } }}
                />
              }
              label={<Typography variant="caption">⭐ Favourites</Typography>}
            />
          </FormGroup>
          {/* Expiry & Strike filters for options */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Expiry from"
              type="date"
              value={expiryFilter}
              onChange={(e) => { setExpiryFilter(e.target.value); setPage(0); }}
              sx={{ minWidth: 150 }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Strike min"
              type="number"
              value={strikeMinFilter}
              onChange={(e) => { setStrikeMinFilter(e.target.value); setPage(0); }}
              sx={{ width: 110 }}
              inputProps={{ min: 0 }}
            />
            <TextField
              size="small"
              label="Strike max"
              type="number"
              value={strikeMaxFilter}
              onChange={(e) => { setStrikeMaxFilter(e.target.value); setPage(0); }}
              sx={{ width: 110 }}
              inputProps={{ min: 0 }}
            />
            {(expiryFilter || strikeMinFilter || strikeMaxFilter) && (
              <Button size="small" variant="text" onClick={() => { setExpiryFilter(''); setStrikeMinFilter(''); setStrikeMaxFilter(''); setPage(0); }}>
                Clear
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, ml: 'auto', alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip label={`Total: ${total}`} size="small" color="primary" />
            <Chip label={`Displayed: ${instruments.length}`} size="small" variant="outlined" />
            {/* Custom pagination — the free DataGrid caps page size at 100, so we
                page server-side and render the full page (250/500/1000) ourselves. */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Rows/page</InputLabel>
              <Select label="Rows/page" value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}>
                {[50, 100, 250, 500, 1000].map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </FormControl>
            <Button size="small" variant="outlined" disabled={page <= 0} onClick={() => setPage(p => Math.max(0, p - 1))}>Prev</Button>
            <Typography variant="caption" sx={{ minWidth: 110, textAlign: 'center' }}>
              {total === 0 ? '0' : `${page * pageSize + 1}–${Math.min((page + 1) * pageSize, total)}`} of {total}
            </Typography>
            <Button size="small" variant="outlined" disabled={(page + 1) * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
          </Box>
        </Box>
      </Paper>

      {/* Data Grid */}
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          apiRef={apiRef}
          rows={instruments}
          columns={columns}
          hideFooter
          loading={loading}
          checkboxSelection
          rowSelectionModel={selectedRows}
          onRowSelectionModelChange={(newSelection) => {
            setSelectedRows(newSelection);
          }}
          processRowUpdate={processRowUpdate}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          onCellEditStart={(params) => {
            // Pre-fill NBUY/NSHORT/BBUY/BSHORT with currentPrice only when cell has no value.
            // Use apiRef.setEditCellValue so only the transient edit input is set — the row data
            // is NOT mutated. If the user navigates away without saving, the value stays null.
            const priceLevelFields = ['nbuy', 'nshort', 'bbuy', 'bshort'];
            if (
              priceLevelFields.includes(params.field) &&
              (params.value === null || params.value === undefined || params.value === '') &&
              params.row.currentPrice
            ) {
              setTimeout(() => {
                apiRef.current.setEditCellValue({
                  id: params.id,
                  field: params.field,
                  value: params.row.currentPrice
                });
              }, 0);
            }
          }}
          slots={{
            toolbar: CustomToolbar
          }}
          sx={{
            border: 0,
            '& .MuiDataGrid-cell:hover': {
              color: 'primary.main',
            },
            '& .MuiDataGrid-cell--editable': {
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
              '&:hover': {
                backgroundColor: 'rgba(25, 118, 210, 0.08)',
              },
            },
          }}
        />
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditCancel} maxWidth="xl" fullWidth>
        <DialogTitle sx={{ pb: 0.5, fontSize: '1rem' }}>Edit Instrument — {editingInstrument?.symbol}</DialogTitle>
        <DialogContent sx={{ pt: '8px !important' }}>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            {/* Row 1: Name + Switches */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 2, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Name"
                value={editingInstrument?.name || ''}
                onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, name: e.target.value } : null)}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {[
                  { key: 'indTracked', label: 'Tracked' },
                  { key: 'indLiveFeed', label: 'Live Feed' },
                  { key: 'indAutoTradeLong', label: 'AT Long' },
                  { key: 'indAutoTradeShort', label: 'AT Short' }
                ].map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Switch
                        size="small"
                        checked={(editingInstrument as any)?.[key] || false}
                        onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, [key]: e.target.checked } : null)}
                      />
                    }
                    label={<Typography variant="caption">{label}</Typography>}
                    sx={{ m: 0, mr: 0.5 }}
                  />
                ))}
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={editingInstrument?.isFavourite || false}
                      onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, isFavourite: e.target.checked } : null)}
                      sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: C.warn }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: C.warn } }}
                    />
                  }
                  label={<Typography variant="caption">⭐ Favourite</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>
            </Box>

            {/* Row 2: Investment Amounts + Trading Config */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5 }}>
              <TextField size="small" type="number" label="EQ Investment" value={editingInstrument?.eqInvestmentAmount || ''}
                onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, eqInvestmentAmount: parseFloat(e.target.value) || 0 } : null)} />
              <TextField size="small" type="number" label="FUT Investment" value={editingInstrument?.futInvestmentAmount || ''}
                onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, futInvestmentAmount: parseFloat(e.target.value) || 0 } : null)} />
              <TextField size="small" type="number" label="OPT Investment (Buy)" value={editingInstrument?.optInvestmentAmountBuy || ''}
                onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, optInvestmentAmountBuy: parseFloat(e.target.value) || 0 } : null)} />
              <TextField size="small" type="number" label="OPT Investment (Sell)" value={editingInstrument?.optInvestmentAmountSell || ''}
                onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, optInvestmentAmountSell: parseFloat(e.target.value) || 0 } : null)} />
            </Box>

            {/* Row 2b: Decimal precision + leverage + range size */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
              <TextField
                size="small" type="number"
                label="Decimal Precision (0–8)"
                helperText="Used everywhere for price display. Falls through: this value → tickSize-derived → 2."
                inputProps={{ min: 0, max: 8, step: 1 }}
                value={(editingInstrument as any)?.decimalPrecision ?? ''}
                onChange={(e) => setEditingInstrument(prev => {
                  if (!prev) return null;
                  if (e.target.value === '') return { ...prev, decimalPrecision: null as any };
                  const n = parseInt(e.target.value, 10);
                  return Number.isFinite(n)
                    ? { ...prev, decimalPrecision: Math.max(0, Math.min(8, n)) }
                    : prev;
                })} />
              <TextField size="small" type="number" label="Leverage" value={(editingInstrument as any)?.leverage ?? ''}
                onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, leverage: e.target.value === '' ? (null as any) : parseFloat(e.target.value) } : null)} />
</Box>

            {/* Trading Configuration */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
              <FormControl size="small" fullWidth>
                <InputLabel>Size orders as</InputLabel>
                <Select value={isAutoSegment(editingInstrument?.tradeSegment) ? '' : String(editingInstrument?.tradeSegment).toUpperCase()} label="Size orders as"
                  onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, tradeSegment: e.target.value } : null)}>
                  <MenuItem value="">Auto ({effectiveSegment(editingInstrument?.exchange, editingInstrument?.instrumentType, '') === 'FUTURES' ? 'Futures' : 'Equity'} on {editingInstrument?.exchange || 'this exchange'})</MenuItem>
                  <MenuItem value="EQUITY">Equity — EQ investment</MenuItem>
                  <MenuItem value="FUTURES">Futures — FUT investment</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Trade Mode</InputLabel>
                <Select value="Naked" label="Trade Mode" disabled>
                  <MenuItem value="Naked">Naked (outright order)</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Current Position</InputLabel>
                <Select value={editingInstrument?.currentPosition || ''} label="Current Position"
                  onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, currentPosition: e.target.value } : null)}>
                  <MenuItem value="">None</MenuItem>
                  <MenuItem value="LONG">Long</MenuItem>
                  <MenuItem value="SHORT">Short</MenuItem>
                  <MenuItem value="FLAT">Flat</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Price Level Monitoring + Watchlist — all in one compact row */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5, gap: 1 }}>
                <Typography variant="caption" color="text.secondary">Price Levels</Typography>
                {editingInstrument?.currentPrice != null && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                    Current: ₹{Number(editingInstrument.currentPrice).toFixed(2)} — click ↑ next to each field to fill
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: 1.5, alignItems: 'center' }}>
                {/* Each price level field has its own ↑ Now button inline */}
                {[
                  { key: 'nbuy', label: 'NBUY (price ≤)', color: C.bull },
                  { key: 'nshort', label: 'NSHORT (price ≥)', color: C.bear },
                  { key: 'bbuy', label: 'BBUY (price ≥)', color: C.neutral },
                  { key: 'bshort', label: 'BSHORT (price ≤)', color: C.accent }
                ].map(({ key, label, color }) => (
                  <TextField
                    key={key}
                    size="small" type="number" label={label}
                    value={(editingInstrument as any)?.[key] ?? ''}
                    onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, [key]: e.target.value === '' ? null : parseFloat(e.target.value) } : null)}
                    InputProps={{
                      sx: { '& input': { color } },
                      endAdornment: editingInstrument?.currentPrice != null ? (
                        <Tooltip title={`Set to current price ₹${Number(editingInstrument.currentPrice).toFixed(2)}`}>
                          <IconButton size="small" tabIndex={-1} sx={{ p: 0.25, color, opacity: 0.7 }}
                            onClick={() => setEditingInstrument(prev => prev && prev.currentPrice != null ? { ...prev, [key]: prev.currentPrice } : prev)}>
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, lineHeight: 1 }}>↑</Typography>
                          </IconButton>
                        </Tooltip>
                      ) : null
                    }}
                    InputLabelProps={{ sx: { color } }}
                  />
                ))}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  <FormControlLabel sx={{ m: 0 }} control={
                    <Switch size="small" checked={editingInstrument?.wlLong || false}
                      onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, wlLong: e.target.checked } : null)} />
                  } label={<Typography variant="caption" sx={{ color: C.bull, fontWeight: 700 }}>WL Long</Typography>} />
                  <FormControlLabel sx={{ m: 0 }} control={
                    <Switch size="small" checked={editingInstrument?.wlShort || false}
                      onChange={(e) => setEditingInstrument(prev => prev ? { ...prev, wlShort: e.target.checked } : null)} />
                  } label={<Typography variant="caption" sx={{ color: C.bear, fontWeight: 700 }}>WL Short</Typography>} />
                </Box>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained" color="primary" disabled={loading}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Edit {selectedRows.length} Instruments</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 1 }}>
            Leave fields blank/unchanged to skip them. Only filled fields will be updated.
          </Typography>
          <Stack spacing={3}>
            {/* Investment Amounts */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Investment Amounts</Typography>
                <Tooltip title="Investment amounts determine position sizing. Qty = (Investment x Leverage) / (Price x Lot Size). Set per segment: EQ (Equity), FUT (Futures), OPT Buy/Sell (Options)." arrow placement="right">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <InfoOutlined fontSize="small" color="action" />
                  </IconButton>
                </Tooltip>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="EQ Investment Amount"
                  value={bulkEditValues.eqInvestmentAmount}
                  onChange={(e) => setBulkEditValues(prev => ({ ...prev, eqInvestmentAmount: e.target.value }))}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="FUT Investment Amount"
                  value={bulkEditValues.futInvestmentAmount}
                  onChange={(e) => setBulkEditValues(prev => ({ ...prev, futInvestmentAmount: e.target.value }))}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="OPT Buy Investment Amount"
                  value={bulkEditValues.optInvestmentAmountBuy}
                  onChange={(e) => setBulkEditValues(prev => ({ ...prev, optInvestmentAmountBuy: e.target.value }))}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="OPT Sell Investment Amount"
                  value={bulkEditValues.optInvestmentAmountSell}
                  onChange={(e) => setBulkEditValues(prev => ({ ...prev, optInvestmentAmountSell: e.target.value }))}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Leverage"
                  value={bulkEditValues.leverage}
                  onChange={(e) => setBulkEditValues(prev => ({ ...prev, leverage: e.target.value }))}
                />
                <TextField
                  fullWidth
                  type="number"
                  label="Decimal Precision (0–8)"
                  helperText="Used everywhere for price display. 0 = whole numbers (MCX), 8 = max for tiny crypto."
                  inputProps={{ min: 0, max: 8, step: 1 }}
                  value={bulkEditValues.decimalPrecision}
                  onChange={(e) => setBulkEditValues(prev => ({ ...prev, decimalPrecision: e.target.value }))}
                />
</Box>
            </Box>

            {/* Boolean Flags */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Trading Flags</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Tracked</InputLabel>
                  <Select
                    value={bulkEditValues.indTracked}
                    label="Tracked"
                    onChange={(e) => setBulkEditValues(prev => ({ ...prev, indTracked: e.target.value }))}
                  >
                    <MenuItem value="">Unchanged</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Live Feed</InputLabel>
                  <Select
                    value={bulkEditValues.indLiveFeed}
                    label="Live Feed"
                    onChange={(e) => setBulkEditValues(prev => ({ ...prev, indLiveFeed: e.target.value }))}
                  >
                    <MenuItem value="">Unchanged</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Auto Long</InputLabel>
                  <Select
                    value={bulkEditValues.indAutoTradeLong}
                    label="Auto Long"
                    onChange={(e) => setBulkEditValues(prev => ({ ...prev, indAutoTradeLong: e.target.value }))}
                  >
                    <MenuItem value="">Unchanged</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Auto Short</InputLabel>
                  <Select
                    value={bulkEditValues.indAutoTradeShort}
                    label="Auto Short"
                    onChange={(e) => setBulkEditValues(prev => ({ ...prev, indAutoTradeShort: e.target.value }))}
                  >
                    <MenuItem value="">Unchanged</MenuItem>
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Trading Configuration */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Trading Configuration</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Size orders as</InputLabel>
                  <Select
                    value={bulkEditValues.tradeSegment}
                    label="Size orders as"
                    onChange={(e) => setBulkEditValues(prev => ({ ...prev, tradeSegment: e.target.value }))}
                  >
                    <MenuItem value="">Unchanged</MenuItem>
                    <MenuItem value="AUTO">Auto (by exchange)</MenuItem>
                    <MenuItem value="EQUITY">Equity</MenuItem>
                    <MenuItem value="FUTURES">Futures</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>

            {/* Watchlist */}
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>Watchlist Classification</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>WL Long</InputLabel>
                  <Select
                    value={bulkEditValues.wlLong}
                    label="WL Long"
                    onChange={(e) => setBulkEditValues(prev => ({ ...prev, wlLong: e.target.value }))}
                  >
                    <MenuItem value="">Unchanged</MenuItem>
                    <MenuItem value="true">Add to Long Watchlist</MenuItem>
                    <MenuItem value="false">Remove from Long Watchlist</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>WL Short</InputLabel>
                  <Select
                    value={bulkEditValues.wlShort}
                    label="WL Short"
                    onChange={(e) => setBulkEditValues(prev => ({ ...prev, wlShort: e.target.value }))}
                  >
                    <MenuItem value="">Unchanged</MenuItem>
                    <MenuItem value="true">Add to Short Watchlist</MenuItem>
                    <MenuItem value="false">Remove from Short Watchlist</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>⭐ Favourite</InputLabel>
                  <Select
                    value={bulkEditValues.isFavourite}
                    label="⭐ Favourite"
                    onChange={(e) => setBulkEditValues(prev => ({ ...prev, isFavourite: e.target.value }))}
                  >
                    <MenuItem value="">Unchanged</MenuItem>
                    <MenuItem value="true">Mark as Favourite</MenuItem>
                    <MenuItem value="false">Remove from Favourites</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkEditOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkEditSave} variant="contained" color="primary" disabled={loading}>
            Update {selectedRows.length} Instruments
          </Button>
        </DialogActions>
      </Dialog>

      {/* Increase Investment Dialog — scale per-symbol investment amounts by a % */}
      <Dialog open={scaleInvestOpen} onClose={() => setScaleInvestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Increase Investment — {selectedRows.length} Instrument(s)</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
            Grow the <strong>existing</strong> per-symbol investment amounts by a percentage for the
            selected instruments — useful as your portfolio scales up. Only the types you tick that
            already have an amount set are increased (empty amounts are left untouched).
          </Typography>
          <TextField
            label="Increase by (%)"
            type="number"
            size="small"
            fullWidth
            value={scaleInvestPercent}
            onChange={(e) => setScaleInvestPercent(e.target.value)}
            helperText="e.g. 10 grows each selected amount by 10%. Negative values reduce."
            sx={{ mb: 2 }}
            inputProps={{ step: '1' }}
          />
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Apply to investment type(s)</Typography>
          <FormGroup>
            {([
              ['eqInvestmentAmount', 'Equity'],
              ['futInvestmentAmount', 'Future'],
              ['optInvestmentAmountBuy', 'Option Buy'],
              ['optInvestmentAmountSell', 'Option Sell'],
            ] as [keyof typeof scaleInvestFields, string][]).map(([key, label]) => (
              <FormControlLabel
                key={key}
                control={
                  <Checkbox
                    size="small"
                    checked={scaleInvestFields[key]}
                    onChange={(e) => setScaleInvestFields(prev => ({ ...prev, [key]: e.target.checked }))}
                  />
                }
                label={label}
              />
            ))}
          </FormGroup>
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={Object.values(scaleInvestFields).every(Boolean)}
                indeterminate={Object.values(scaleInvestFields).some(Boolean) && !Object.values(scaleInvestFields).every(Boolean)}
                onChange={(e) => {
                  const v = e.target.checked;
                  setScaleInvestFields({ eqInvestmentAmount: v, futInvestmentAmount: v, optInvestmentAmountBuy: v, optInvestmentAmountSell: v });
                }}
              />
            }
            label={<strong>All of the above</strong>}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScaleInvestOpen(false)}>Cancel</Button>
          <Button onClick={handleScaleInvestSave} variant="contained" color="success" disabled={loading}>
            Apply to {selectedRows.length} Instrument(s)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Zerodha Import Confirmation Dialog */}
      <Dialog open={zerodhaDialogOpen} onClose={() => setZerodhaDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import Instruments from Zerodha</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1 }}>
            Fetches the <strong>latest instrument list live</strong> from Zerodha's public API (<code>api.kite.trade/instruments</code>).
          </Typography>
          <Stack spacing={2} sx={{ mt: 1, mb: 1 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Exchange</InputLabel>
              <Select value={zerodhaImportExchange} label="Exchange" onChange={(e) => setZerodhaImportExchange(e.target.value)}>
                <MenuItem value="ALL">ALL Exchanges (NSE, BSE, NFO, BFO, CDS, MCX…)</MenuItem>
                <MenuItem value="NSE">NSE only</MenuItem>
                <MenuItem value="BSE">BSE only</MenuItem>
                <MenuItem value="NFO">NFO only (F&O)</MenuItem>
                <MenuItem value="BFO">BFO only (BSE F&O)</MenuItem>
                <MenuItem value="CDS">CDS only (Currency)</MenuItem>
                <MenuItem value="MCX">MCX only (Commodity)</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth>
              <InputLabel>Instrument Type</InputLabel>
              <Select value={zerodhaImportType} label="Instrument Type" onChange={(e) => setZerodhaImportType(e.target.value)}>
                <MenuItem value="ALL">ALL Types (EQ, FUT, CE, PE…)</MenuItem>
                <MenuItem value="EQ">EQ only (Equity)</MenuItem>
                <MenuItem value="FUT">FUT only (Futures)</MenuItem>
                <MenuItem value="CE">CE only (Call Options)</MenuItem>
                <MenuItem value="PE">PE only (Put Options)</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            "ALL" may import 500,000+ rows (options). Use specific filters to import only what you need.
          </Typography>
          <Typography variant="body2" color="warning.main">
            This may take 1–5 minutes. Existing instruments will be updated; new ones added.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setZerodhaDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleImportFromZerodha} variant="contained" color="success">
            Import
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Help / How-to dialog */}
      <Dialog open={helpOpen} onClose={() => setHelpOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Instruments — How it works</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Purpose</Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            The Instruments table is the master list of every symbol the system knows about.
            Two layers store data: the global <code>instruments</code> table holds market
            fields (lot size, tick size, leverage, decimal precision, expiry, broker token,
            current price); per-user fields (price levels, investment amounts, watchlist
            flags, trade segment / mode, auto-trade flags, favourite) live in{' '}
            <code>user_instrument_prefs</code>.
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Filters</Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1.5 }}>
            <b>Exchange</b>: NSE / BSE / NFO / BFO / MCX / CDS / DELTA.<br/>
            <b>Type</b>: EQ = cash equity; FUT = futures; <b>CE/PE</b> = option (this covers Delta options too — they are categorised as CE / PE on import based on the product's call/put metadata); PERPETUAL = crypto perp; SPOT = crypto spot.<br/>
            <b>Tracked</b> / <b>Not Tracked</b>: alerts and price-level monitoring only run for tracked symbols.<br/>
            <b>NSE F&amp;O</b>: limit NSE equities to the underlyings that have F&amp;O contracts (the stocks you can actually trade derivatives on).<br/>
            <b>Auto Long</b> / <b>Auto Short</b>: show only symbols whose per-direction auto-trade flag is ON.<br/>
            <b>Live Feed</b>: receive streaming price data (used by charts and trailing SL).<br/>
            The <i>readymade</i> chips combine several of these in one click (e.g. "NSE F&amp;O · Tracked").
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Auto-trade flags (AT Long / AT Short)</Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1.5 }}>
            Per-symbol, per-direction gates for autonomous trading. <b>AT Long</b> ON = RockBand BUY signals for this
            symbol auto-execute; <b>AT Short</b> ON = SHORT signals auto-execute. Both also require the global
            <b> Auto Trade</b> and <b>Order Placement</b> switches ON (Dashboard). When a direction's flag is OFF the
            matching alert is saved with an <i>AUTO SKIPPED · AL OFF / AS OFF</i> chip and no order is placed — you can
            still Execute it manually. Exits are never gated by these flags.
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Investment &amp; quantity</Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Quantity is auto-computed when orders are placed, using:
            <br/><code>qty = floor((investment × leverage) / (entryPrice × lotSize))</code>
            <br/>For F&amp;O and MCX, qty is rounded down to a lot-size multiple. For DELTA crypto, lotSize is usually 1.
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Price levels (NBUY/NSHORT/BBUY/BSHORT)</Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1.5 }}>
            <b>NBUY</b> — alert when price drops to this level (mean-reversion buy).<br/>
            <b>NSHORT</b> — alert when price rises to this level (mean-reversion short).<br/>
            <b>BBUY</b> — alert when price breaks above this level (breakout buy).<br/>
            <b>BSHORT</b> — alert when price breaks below this level (breakout short).<br/>
            Manage these inline on the Dashboard's Price Level Watch card. <i>Side-clearing rule:</i> firing NBUY or BBUY clears <i>both</i> long-side levels for the symbol; firing NSHORT or BSHORT clears both short-side levels.
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
            Symbol downloads — Import from Zerodha
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
            <b>Source</b>: <code>https://api.kite.trade/instruments</code> — Zerodha's public CSV dump of every tradable instrument. No auth required.<br/>
            <b>CSV columns parsed</b> (in order): <code>instrument_token, exchange_token, tradingsymbol, name, last_price, expiry, strike, tick_size, lot_size, instrument_type, segment, exchange</code>.
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
            <b>Default filter</b> (no params): NSE equity (<code>segment=NSE, type=EQ</code>) + NFO futures (<code>segment=NFO-FUT, type=FUT</code>). Options are excluded from the default sweep because the dump contains 50,000+ rows mostly weekly options that go stale fast — pick <i>Type</i> = CE/PE in the Bulk Import dialog if you want them.
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
            <b>Explicit filters</b>: when <code>exchangeFilter</code>, <code>typeFilter</code>, or <code>segmentFilter</code> is supplied (via the Bulk Import dialog), only matching rows are imported.
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 1.5 }}>
            <b>Update strategy</b>: <code>INSERT … ON CONFLICT (symbol, exchange) DO UPDATE</code> in batches of 500. The conflict path updates only market data fields (<code>name, instrumentType, segment, expiry, strike, tickSize, lotSize, instrumentToken, updatedAt</code>); user fields (<code>isFavourite, nbuy/nshort/bbuy/bshort, wlLong/wlShort, tradeSegment, tradeMode, indAutoTradeLong/Short, investment amounts, currentPosition</code>) are <b>NEVER</b> overwritten — they are preserved at their existing values on every re-import.
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>
            Symbol downloads — Import from Delta Exchange
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
            <b>Source</b>: Delta Exchange India's authenticated <code>GET /v2/products</code> endpoint via the user's API key.
          </Typography>
          <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
            <b>Per-product processing</b>:
          </Typography>
          <Box component="ul" sx={{ pl: 3, m: 0, mb: 1 }}>
            <li>
              <Typography variant="body2"><b>Skip</b> products without a <code>symbol</code> field.</Typography>
            </li>
            <li>
              <Typography variant="body2"><b>Categorise</b> by <code>product_type</code> / <code>option_type</code> → CE (call), PE (put), PERPETUAL, FUTURES, or SPOT.</Typography>
            </li>
            <li>
              <Typography variant="body2"><b>Leverage</b> = <code>1 / initial_margin</code>, clamped to [1, 200].</Typography>
            </li>
            <li>
              <Typography variant="body2"><b>Lot size</b> = <code>contract_value</code>; if missing, fall back to underlying-aware defaults (BTC=0.001, ETH=0.01, SOL=0.1, XRP=1, …).</Typography>
            </li>
            <li>
              <Typography variant="body2"><b>Tick size</b> = <code>tick_size</code> (default 0.01 if missing). Currency = USD.</Typography>
            </li>
            <li>
              <Typography variant="body2"><b>Expiry</b> = <code>settlement_time</code> (used by the Expired Instruments cleanup task).</Typography>
            </li>
          </Box>
          <Typography variant="body2" component="div" sx={{ mb: 1.5 }}>
            <b>Update strategy</b>: row-by-row <code>INSERT</code> for new symbols, <code>UPDATE</code> for existing — only market fields (<code>name, instrumentType, lotSize, tickSize, leverage, strike, expiry, underlying, currency, instrumentToken, updatedAt</code>). User fields are preserved.
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Bulk edit</Typography>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Select multiple rows, then <b>Bulk Edit</b> to apply one change to all. Whitelisted fields cover <i>investment amounts, leverage, decimal precision, watchlist, auto-trade, tracked, live feed, segment / mode</i>; raw market fields (lotSize, tickSize, strike, expiry, instrumentToken) are not bulk-editable to avoid breaking referential integrity.
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 1 }}>Excel backup</Typography>
          <Typography variant="body2">
            <b>Export Excel</b> downloads every row including your custom fields.
            <b> Restore Excel</b> uploads the file and restores user fields (price levels,
            investment amounts, flags, decimal precision, leverage) on the matching <i>symbol + exchange</i> — useful when re-importing from exchange wipes data you'd manually configured. Symbols not present in the local DB are silently skipped (count surfaced in the toast).
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstrumentsPage;
